import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ShieldCheck, ScanHeart } from "lucide-react";
import Logo from "./Logo";

const EASE = [0.16, 1, 0.3, 1];

const HERO_LINES = ["Care, aligned", "by intelligence."];

const MANIFESTO = [
  { num: "01", title: "Precision", copy: "Diagnosis-grade signal from the noisiest clinical data." },
  { num: "02", title: "Empathy", copy: "Built for clinicians and patients, not dashboards." },
  { num: "03", title: "Foresight", copy: "Surfacing risk long before symptoms appear." },
];

const MARQUEE_ITEMS = [
  "PREDICTIVE DIAGNOSTICS",
  "CLINICAL WORKFLOW AI",
  "HIPAA-GRADE SECURITY",
  "REAL-TIME PATIENT SIGNALS",
  "FDA-AWARE PIPELINES",
  "HUMAN-FIRST INTELLIGENCE",
];

function MaskedLine({ text, delay, className }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: EASE, delay }}
        className={`block ${className}`}
      >
        {text}
      </motion.span>
    </span>
  );
}

export default function BrandPanel() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const blobX = useTransform(sx, (v) => v * 50);
  const blobY = useTransform(sy, (v) => v * 35);
  const blob2X = useTransform(sx, (v) => v * -35);
  const blob2Y = useTransform(sy, (v) => v * -25);

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      data-testid="brand-panel"
      onMouseMove={onMouseMove}
      className="relative hidden lg:flex flex-col justify-between overflow-hidden border-r border-white/10 bg-[#050814]"
    >
      {/* Aurora blobs with mouse parallax */}
      <motion.div
        style={{ x: blobX, y: blobY }}
        className="absolute -top-32 -left-32 w-[34rem] h-[34rem] rounded-full bg-[#0066FF]/30 blur-[120px]"
      />
      <motion.div
        style={{ x: blob2X, y: blob2Y }}
        className="absolute bottom-24 -right-24 w-[30rem] h-[30rem] rounded-full bg-[#5E2BFF]/30 blur-[120px]"
      />
      <div className="absolute top-1/2 left-1/3 w-[22rem] h-[22rem] rounded-full bg-[#00F0FF]/10 blur-[120px]" />

      {/* Grid texture */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />

      <div className="relative z-10 p-12 xl:p-16">
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <Logo />
        </motion.div>
      </div>

      {/* Kinetic hero */}
      <div data-testid="brand-hero" className="relative z-10 px-12 xl:px-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="text-[11px] tracking-[0.45em] uppercase text-[#00F0FF]/80 font-light mb-8"
        >
          The clinical intelligence platform
        </motion.p>
        <h1 className="font-display font-medium text-5xl xl:text-6xl leading-[1.04] tracking-[-0.02em] text-white">
          {HERO_LINES.map((line, i) => (
            <MaskedLine
              key={line}
              text={line}
              delay={0.35 + i * 0.16}
              className={i === 1 ? "text-gradient-brand pb-2" : ""}
            />
          ))}
        </h1>

        {/* Manifesto chapters */}
        <div data-testid="manifesto-chapters" className="mt-14 space-y-0 max-w-md">
          {MANIFESTO.map((chapter, i) => (
            <motion.div
              key={chapter.num}
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.9 + i * 0.15 }}
              className="group flex items-baseline gap-6 py-4 border-t border-white/10 last:border-b"
              data-testid={`manifesto-chapter-${chapter.num}`}
            >
              <span className="font-display text-xs text-[#00F0FF]/70 tracking-widest">
                {chapter.num}
              </span>
              <div>
                <p className="font-display text-base text-white tracking-tight transition-colors duration-300 group-hover:text-[#00F0FF]">
                  {chapter.title}
                </p>
                <p className="text-sm text-white/45 font-light mt-0.5">{chapter.copy}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Floating glass stat chip */}
      <motion.div
        data-testid="brand-stat-chip"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.4, duration: 0.8, ease: EASE }}
        className="absolute top-[22%] right-10 xl:right-16 z-10"
      >
        <div className="glass rounded-2xl px-5 py-4 animate-float-slow">
          <div className="flex items-center gap-2.5">
            <ScanHeart className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.25em] uppercase text-white/50">
              Live triage signal
            </span>
          </div>
          <p className="font-display text-2xl text-white mt-2 tracking-tight">
            99.2<span className="text-[#00F0FF]">%</span>
          </p>
          <p className="text-[11px] text-white/40 font-light">alignment accuracy</p>
        </div>
      </motion.div>

      {/* Editorial marquee */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="relative z-10 border-t border-white/10"
        data-testid="brand-marquee"
      >
        <Marquee speed={28} gradient={false} className="py-5">
          {MARQUEE_ITEMS.map((item) => (
            <span key={item} className="flex items-center">
              <span className="text-[11px] tracking-[0.4em] text-white/35 font-light mx-10">
                {item}
              </span>
              <ShieldCheck className="w-3 h-3 text-[#5E2BFF]/60" strokeWidth={1.5} />
            </span>
          ))}
        </Marquee>
      </motion.div>
    </section>
  );
}
